// Translations loaded on demand (REL-QA-04, docs/plan/12-frontend.md §10 bundle budget): the web
// app fetches the namespaces of the language in use, each JSON file its own chunk, and those of
// another language only when the person switches to it. Nothing is bundled in the initial script.
import type { AppLocale } from '@batiflow/i18n';
import type { BackendModule, ResourceKey } from 'i18next';

/** The namespaces of @batiflow/i18n that the web app uses (not email, pdf, backoffice, keycloak). */
export const WEB_NAMESPACES = [
  'common',
  'web',
  'ui',
  'clients',
  'tasks',
  'projects',
  'documents',
  'viewer',
  'search',
  'chat',
  'calendar',
  'meetings',
  'notifications',
  'spaces',
  'finance',
  'planning',
  'field',
  'transmittals',
  'shareLinks',
  'tenders',
  'suppliers',
  'profitability',
  'accounting',
  'management',
  'signatures',
  'observations',
] as const;
export type WebNamespace = (typeof WEB_NAMESPACES)[number];

/**
 * Namespaces loaded only when a screen asks for them (`useTranslation('<ns>')`), never before the
 * first render: the page guide is big and read by few.
 */
export const LAZY_NAMESPACES = ['guide'] as const;
export type LazyNamespace = (typeof LAZY_NAMESPACES)[number];

type Loader = () => Promise<{ default: ResourceKey }>;

// Literal paths: the bundler turns each file into its own chunk.
const LOADERS: Record<AppLocale, Record<WebNamespace | LazyNamespace, Loader>> = {
  fr: {
    guide: () => import('@batiflow/i18n/locales/fr/guide.json'),
    common: () => import('@batiflow/i18n/locales/fr/common.json'),
    web: () => import('@batiflow/i18n/locales/fr/web.json'),
    ui: () => import('@batiflow/i18n/locales/fr/ui.json'),
    clients: () => import('@batiflow/i18n/locales/fr/clients.json'),
    tasks: () => import('@batiflow/i18n/locales/fr/tasks.json'),
    projects: () => import('@batiflow/i18n/locales/fr/projects.json'),
    documents: () => import('@batiflow/i18n/locales/fr/documents.json'),
    viewer: () => import('@batiflow/i18n/locales/fr/viewer.json'),
    search: () => import('@batiflow/i18n/locales/fr/search.json'),
    chat: () => import('@batiflow/i18n/locales/fr/chat.json'),
    calendar: () => import('@batiflow/i18n/locales/fr/calendar.json'),
    meetings: () => import('@batiflow/i18n/locales/fr/meetings.json'),
    notifications: () => import('@batiflow/i18n/locales/fr/notifications.json'),
    spaces: () => import('@batiflow/i18n/locales/fr/spaces.json'),
    finance: () => import('@batiflow/i18n/locales/fr/finance.json'),
    planning: () => import('@batiflow/i18n/locales/fr/planning.json'),
    field: () => import('@batiflow/i18n/locales/fr/field.json'),
    transmittals: () => import('@batiflow/i18n/locales/fr/transmittals.json'),
    shareLinks: () => import('@batiflow/i18n/locales/fr/shareLinks.json'),
    tenders: () => import('@batiflow/i18n/locales/fr/tenders.json'),
    suppliers: () => import('@batiflow/i18n/locales/fr/suppliers.json'),
    profitability: () => import('@batiflow/i18n/locales/fr/profitability.json'),
    accounting: () => import('@batiflow/i18n/locales/fr/accounting.json'),
    management: () => import('@batiflow/i18n/locales/fr/management.json'),
    signatures: () => import('@batiflow/i18n/locales/fr/signatures.json'),
    observations: () => import('@batiflow/i18n/locales/fr/observations.json'),
  },
  ar: {
    guide: () => import('@batiflow/i18n/locales/ar/guide.json'),
    common: () => import('@batiflow/i18n/locales/ar/common.json'),
    web: () => import('@batiflow/i18n/locales/ar/web.json'),
    ui: () => import('@batiflow/i18n/locales/ar/ui.json'),
    clients: () => import('@batiflow/i18n/locales/ar/clients.json'),
    tasks: () => import('@batiflow/i18n/locales/ar/tasks.json'),
    projects: () => import('@batiflow/i18n/locales/ar/projects.json'),
    documents: () => import('@batiflow/i18n/locales/ar/documents.json'),
    viewer: () => import('@batiflow/i18n/locales/ar/viewer.json'),
    search: () => import('@batiflow/i18n/locales/ar/search.json'),
    chat: () => import('@batiflow/i18n/locales/ar/chat.json'),
    calendar: () => import('@batiflow/i18n/locales/ar/calendar.json'),
    meetings: () => import('@batiflow/i18n/locales/ar/meetings.json'),
    notifications: () => import('@batiflow/i18n/locales/ar/notifications.json'),
    spaces: () => import('@batiflow/i18n/locales/ar/spaces.json'),
    finance: () => import('@batiflow/i18n/locales/ar/finance.json'),
    planning: () => import('@batiflow/i18n/locales/ar/planning.json'),
    field: () => import('@batiflow/i18n/locales/ar/field.json'),
    transmittals: () => import('@batiflow/i18n/locales/ar/transmittals.json'),
    shareLinks: () => import('@batiflow/i18n/locales/ar/shareLinks.json'),
    tenders: () => import('@batiflow/i18n/locales/ar/tenders.json'),
    suppliers: () => import('@batiflow/i18n/locales/ar/suppliers.json'),
    profitability: () => import('@batiflow/i18n/locales/ar/profitability.json'),
    accounting: () => import('@batiflow/i18n/locales/ar/accounting.json'),
    management: () => import('@batiflow/i18n/locales/ar/management.json'),
    signatures: () => import('@batiflow/i18n/locales/ar/signatures.json'),
    observations: () => import('@batiflow/i18n/locales/ar/observations.json'),
  },
  en: {
    guide: () => import('@batiflow/i18n/locales/en/guide.json'),
    common: () => import('@batiflow/i18n/locales/en/common.json'),
    web: () => import('@batiflow/i18n/locales/en/web.json'),
    ui: () => import('@batiflow/i18n/locales/en/ui.json'),
    clients: () => import('@batiflow/i18n/locales/en/clients.json'),
    tasks: () => import('@batiflow/i18n/locales/en/tasks.json'),
    projects: () => import('@batiflow/i18n/locales/en/projects.json'),
    documents: () => import('@batiflow/i18n/locales/en/documents.json'),
    viewer: () => import('@batiflow/i18n/locales/en/viewer.json'),
    search: () => import('@batiflow/i18n/locales/en/search.json'),
    chat: () => import('@batiflow/i18n/locales/en/chat.json'),
    calendar: () => import('@batiflow/i18n/locales/en/calendar.json'),
    meetings: () => import('@batiflow/i18n/locales/en/meetings.json'),
    notifications: () => import('@batiflow/i18n/locales/en/notifications.json'),
    spaces: () => import('@batiflow/i18n/locales/en/spaces.json'),
    finance: () => import('@batiflow/i18n/locales/en/finance.json'),
    planning: () => import('@batiflow/i18n/locales/en/planning.json'),
    field: () => import('@batiflow/i18n/locales/en/field.json'),
    transmittals: () => import('@batiflow/i18n/locales/en/transmittals.json'),
    shareLinks: () => import('@batiflow/i18n/locales/en/shareLinks.json'),
    tenders: () => import('@batiflow/i18n/locales/en/tenders.json'),
    suppliers: () => import('@batiflow/i18n/locales/en/suppliers.json'),
    profitability: () => import('@batiflow/i18n/locales/en/profitability.json'),
    accounting: () => import('@batiflow/i18n/locales/en/accounting.json'),
    management: () => import('@batiflow/i18n/locales/en/management.json'),
    signatures: () => import('@batiflow/i18n/locales/en/signatures.json'),
    observations: () => import('@batiflow/i18n/locales/en/observations.json'),
  },
};

/** An i18next backend over LOADERS. */
export const localeBackend: BackendModule = {
  type: 'backend',
  init: () => undefined,
  read(language, namespace, callback) {
    const load = (LOADERS as Partial<Record<string, Partial<Record<string, Loader>>>>)[language]?.[
      namespace
    ];
    if (!load) {
      callback(new Error(`no translations for ${language}/${namespace}`), false);
      return;
    }
    load().then(
      (module) => {
        callback(null, module.default);
      },
      (error: unknown) => {
        callback(error instanceof Error ? error : new Error(String(error)), false);
      },
    );
  },
};
